import {
  uploadW9Form,
  fetchW9Status,
  fetchUserPayouts,
  withdrawEarnedAmount,
  createStripePayoutAccount,
  deleteStripePayoutAccount,
} from "../../api/Affiliate";
import Swal from "sweetalert2";
import { Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import "../../assets/css/affiliateProgram.css";
import { getCurrentUser } from "../../redux/selectors";
import { toastrOnTopCenter } from "../../utils/toastr";
import { globalImages } from "../../utils/staticImages";
import WithdrawAmount from "./components/WithdrawAmount";
import React, { useRef, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AuthenticatedLayout from "../../layouts/AuthenticatedLayout";
import DotPulseBackdrop from "../shared/components/DotPulseBackdrop";
import { documentPreview, truncateFileName } from "../../utils/generalHelper";

function InitiateWithdraw() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [w9Data, setW9Data] = useState("");
  const [bankData, setBankData] = useState("");
  const status = searchParams.get("status") ?? "";
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const currentUser = useSelector((state) => getCurrentUser(state));

  const w9Empty = w9Data == null;
  const w9Pending = w9Data?.status === "pending";
  const w9Rejected = w9Data?.status === "rejected";
  const w9Approved = w9Data?.status === "approved";
  const HAS_ACTIVE_TRIAL = currentUser.trial_user;
  const IS_PAYOUTS_PAUSE = bankData?.payout_setting?.is_pause;
  const HAS_ACTIVE_SUBSCRIPTION = currentUser.has_active_subscription;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await fetchUserPayouts(status);
      setBankData(response.data);
    } catch (error) {
      console.error("error", error);
      toastrOnTopCenter(
        "Failed to fetch data. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchW9Data = async () => {
    setIsLoading(true);
    try {
      const response = await fetchW9Status();
      setW9Data(response.data);
    } catch (error) {
      console.error("error", error);
      toastrOnTopCenter(
        "Failed to fetch data. Please try again later.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchW9Data();
    // eslint-disable-next-line
  }, []);

  const createPayoutAccount = async () => {
    if (!HAS_ACTIVE_SUBSCRIPTION || HAS_ACTIVE_TRIAL) {
      toastrOnTopCenter("Please purchase a subscription plan first.", "error");
      return;
    }
    if (w9Empty || w9Pending || w9Rejected) {
      toastrOnTopCenter(
        "Accounts can only be linked once your W-9 form is approved.",
        "error"
      );
      return;
    }
    if (w9Rejected) {
      toastrOnTopCenter("Your W-9 form is Rejected. Upload it again!", "error");
      return;
    }
    Swal.fire({
      title: "Confirm Redirect",
      text: "You will be redirected to Stripe to complete the setup.",
      icon: "warning",
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-secondary",
      },
      confirmButtonText: "OK",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await createStripePayoutAccount();
          const url = response?.data?.url;
          if (url) {
            window.location.href = url;
          }
          if (response?.message === "Account is already existed") {
            toastrOnTopCenter(response.message, "error");
          }
        } catch (error) {
          console.error("error", error);
          toastrOnTopCenter(
            "Failed to create a payout account. Please try again.",
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const deletePayoutAccount = async () => {
    Swal.fire({
      title: "Remove Account",
      text: "Are you sure to remove this account.",
      icon: "warning",
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-secondary",
      },
      confirmButtonText: "OK",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await deleteStripePayoutAccount();
          fetchData();
          setWithdrawAmount("");
          toastrOnTopCenter(
            response?.message,
            response?.status === false ? "error" : "success"
          );
        } catch (error) {
          console.error("error", error);
          toastrOnTopCenter(
            "Failed to remove your payout account. Please try again later.",
            "error"
          );
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const handleWithdraw = async () => {
    if (!HAS_ACTIVE_SUBSCRIPTION || HAS_ACTIVE_TRIAL) {
      toastrOnTopCenter("Please purchase a subscription plan first.", "error");
      return;
    }
    if (w9Pending || w9Rejected || w9Empty) {
      toastrOnTopCenter(
        "Withdrawals will be enabled once your W-9 form is approved.",
        "error"
      );
      return;
    }
    if (IS_PAYOUTS_PAUSE) {
      toastrOnTopCenter(
        bankData.payout_setting.message || "Payouts disabled.",
        "error"
      );
      return;
    }
    if (!withdrawAmount) {
      toastrOnTopCenter("Please enter a valid amount to withdraw.", "error");
      return;
    }
    const balance = bankData?.balance ?? 0;
    const minAmount = 49;
    
    if (Number(withdrawAmount) < minAmount) {
      toastrOnTopCenter(
        `Minimum withdrawal amount is $${minAmount}. Your current balance is $${balance.toFixed(1)}.`,
        "error"
      );
      return;
    }
    
    if (Number(withdrawAmount) > balance) {
      toastrOnTopCenter(
        `Insufficient balance. Your current balance is $${balance.toFixed(1)}. The maximum amount you can withdraw is $${balance.toFixed(1)}.`,
        "error"
      );
      return;
    }

    Swal.fire({
      title: "Confirm Withdrawals",
      text: `Are you sure you want to withdraw $${withdrawAmount}?`,
      icon: "warning",
      showCancelButton: true,
      buttonsStyling: false,
      customClass: {
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-secondary",
      },
      confirmButtonText: "OK",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);
        try {
          const response = await withdrawEarnedAmount({
            amount: withdrawAmount,
          });
          // Await fetchData to ensure balance is updated before allowing next payout
          await fetchData();
          toastrOnTopCenter(response?.message, "success");
          setIsLoading(false);
          setWithdrawAmount("");
        } catch (error) {
          console.error("error", error);
          // Check if it's a Stripe balance error and show generic message
          const errorMessage = error?.response?.data?.message || error?.message || "";
          const isStripeBalanceError = (
            errorMessage.toLowerCase().includes('insufficient') ||
            errorMessage.toLowerCase().includes('balance') ||
            errorMessage.toLowerCase().includes('funds') ||
            errorMessage.toLowerCase().includes('stripe') ||
            errorMessage.toLowerCase().includes('not enough') ||
            errorMessage.toLowerCase().includes('cannot create payout')
          );
          
          if (isStripeBalanceError) {
            toastrOnTopCenter(
              "Payouts are temporarily on hold, please try again later",
              "error"
            );
          } else {
            toastrOnTopCenter(
              errorMessage || "Failed to withdraw payment. Please try again later.",
              "error"
            );
          }
        } finally {
          setIsLoading(false);
        }
      }
    });
  };

  const selectW9Form = async (event) => {
    const file = event.target.files[0];
    if (
      file &&
      (file.type === "application/pdf" || file.type.includes("word"))
    ) {
      setSelectedFile(file);
    } else {
      toastrOnTopCenter("Please select a valid PDF or DOC file.", "error");
    }
  };

  const updateW9Form = async () => {
    if (!selectedFile) {
      toastrOnTopCenter("No file selected.", "error");
      return;
    }
    setIsLoading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const response = await uploadW9Form(formData);
      toastrOnTopCenter(
        response?.data?.message || "File uploaded successfully!",
        "success"
      );
      setSelectedFile(null);
      fetchW9Data();
    } catch (error) {
      toastrOnTopCenter("Failed to upload file. Please try again.", "error");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileSelection = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const fillablew9Form = (state) => {
    const isSmallScreen = window.innerWidth <= 768;

    if (isSmallScreen) {
      Swal.fire({
        title: "Fill W9 Form!",
        text: "Oops! This form is only available on desktop or laptop. Please switch to a larger screen to continue.",
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: "btn btn-secondary",
        },
        confirmButtonText: "OK",
      });
    } else {
      if (state === "create") {
        navigate("/affiliate-program/initiate-withdraw/w9-form?state=create");
      } else if (state === "edit") {
        navigate(
          `/affiliate-program/initiate-withdraw/w9-form?state=${state}`,
          {
            state: { w9Data },
          }
        );
      } else if (state === "preview") {
        window.location.href =
          "/affiliate-program/initiate-withdraw/w9-form/preview";
      }
    }
  };

  return (
    <div className="row justify-content-center mt-3">
      <div className="col-11 px-0">
        <div className="row">
          <div className="col-md-6 col-12 pr-2">
            {/* upload w9 form */}
            <div className="card mb-3">
              {!w9Rejected && w9Data && (
                <div className="card-body pt-3">
                  <div className="row mt-2 border-dashed-2px py-2">
                    <div className="col-6 d-center text-default">
                      <i className="fa fa-file-o text-xl p-2"></i>
                      <span>
                        {w9Data.file_path
                          ? selectedFile
                            ? truncateFileName(selectedFile.name, 15)
                            : truncateFileName(w9Data.original_name, 15)
                          : "W9 Form"}
                      </span>
                    </div>
                    <div className="col-6 d-center">
                      {(w9Rejected || w9Pending) && w9Data.file_path && (
                        <>
                          {w9Data.file_path ? (
                            <>
                              {!selectedFile ? (
                                <>
                                  <label className="btn btn-default px-4 py-1 mb-0">
                                    Edit
                                    <input
                                      type="file"
                                      accept=".pdf,.doc,.docx"
                                      style={{ display: "none" }}
                                      onChange={selectW9Form}
                                    />
                                  </label>
                                </>
                              ) : (
                                <button
                                  className="btn btn-primary px-4 py-1"
                                  onClick={updateW9Form}
                                  disabled={isLoading}
                                >
                                  {isLoading ? "Uploading..." : "Upload"}
                                </button>
                              )}
                            </>
                          ) : (
                            <button
                              className="btn btn-default px-4 py-1"
                              onClick={() => fillablew9Form("edit")}
                            >
                              Edit
                            </button>
                          )}
                        </>
                      )}
                      {!selectedFile && (
                        <button
                          className="btn btn-default px-2 py-1"
                          onClick={() =>
                            w9Data.file_path
                              ? documentPreview(w9Data.file_path)
                              : fillablew9Form("preview")
                          }
                        >
                          Preview
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="text-left pt-2">
                    <span style={{ fontSize: 14, color: "#000" }}>
                      {w9Pending && (
                        <>
                          <span className="text-danger">●</span> Your W-9 form
                          is under review and will be approved soon
                        </>
                      )}
                      {w9Rejected && (
                        <>
                          <span
                            className="text-danger border border-danger rounded-pill mr-2"
                            style={{
                              padding: "0px 3px 0px 4px",
                              fontSize: "15px",
                            }}
                          >
                            &#10005;
                          </span>
                          <span style={{ fontSize: 16 }}>Rejected</span>
                        </>
                      )}
                      {w9Approved && (
                        <>
                          <span
                            className="text-success border border-success rounded-pill mr-2"
                            style={{
                              padding: "0px 4px 1px 5px",
                              fontSize: "15px",
                            }}
                          >
                            &#x2713;
                          </span>
                          <span style={{ fontSize: 16 }}>Approved</span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              )}
              {!w9Data || w9Rejected ? (
                <div className="card-body px-4 pb-3">
                  <div className="row border-dashed-2px rounded-sm py-2">
                    <div className="col-2 d-center cursor-pointer">
                      <Tooltip
                        title="Upload File"
                        placement="top"
                        className="pl-4"
                      >
                        <span>
                          <img
                            alt="upload"
                            onClick={triggerFileSelection}
                            src={globalImages.cloud_upload}
                            style={{
                              width: 30,
                              height: 30,
                              aspectRatio: "1",
                              objectFit: "cover",
                              position: "relative",
                            }}
                          />
                          <input
                            type="file"
                            accept=".pdf,.doc,.docx"
                            style={{ display: "none" }}
                            ref={fileInputRef}
                            onChange={selectW9Form}
                          />
                        </span>
                      </Tooltip>
                    </div>
                    <div className="col-6 text-left lh-100 py-3">
                      {selectedFile ? (
                        truncateFileName(selectedFile.name, 15)
                      ) : (
                        <>
                          <span style={{ fontSize: 14, color: "#000" }}>
                            Select a file or upload
                          </span>{" "}
                          <br />
                          <span style={{ fontSize: 10, color: "#989898" }}>
                            add the feature to upload W9 Form
                          </span>{" "}
                          <br />
                          <span style={{ fontSize: 12, color: "#989898" }}>
                            (Upload Document)
                          </span>
                        </>
                      )}
                    </div>
                    <div className="col-4 d-center">
                      {!selectedFile ? (
                        <>
                          <label className="btn btn-outline-primary">
                            Select File
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx"
                              style={{ display: "none" }}
                              onChange={selectW9Form}
                            />
                          </label>
                        </>
                      ) : (
                        <button
                          className="btn btn-primary"
                          onClick={updateW9Form}
                          disabled={isLoading}
                        >
                          {isLoading ? "Uploading..." : "Upload"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="d-center w-100">
                    <hr className="w-100 m-3" />
                    <span>Or</span>
                    <hr className="w-100 m-3" />
                  </div>
                  <div className="d-center">
                    <button
                      className="btn btn-default w-100 mx-3"
                      onClick={() => fillablew9Form("create")}
                    >
                      Fill the form here!
                    </button>
                  </div>
                  <div className="text-left pt-2">
                    <span style={{ fontSize: 14, color: "#000" }}>
                      {w9Rejected ? (
                        <>
                          <span
                            className="text-danger border border-danger rounded-pill mr-2"
                            style={{
                              padding: "0px 3px 0px 4px",
                              fontSize: "15px",
                            }}
                          >
                            &#10005;
                          </span>
                          <span style={{ fontSize: 16 }}>Rejected</span>
                        </>
                      ) : (
                        <>
                          <span className="text-success">●</span> Upload your
                          W-9 form for admin approval to enable withdrawals
                        </>
                      )}
                    </span>
                  </div>
                </div>
              ) : (
                ""
              )}
            </div>

            {/* Link Bank account */}
            <div className="card">
              <div className="card-body pt-3">
                <h3>Linked Accounts</h3>
                <span>All transactions are secure and encrypted.</span>
                {bankData?.bank_account?.bank_name && (
                  <div className="row mt-2">
                    <div className="col-3 d-center">
                      <span className="avatar avatar-lg border rounded-pill bg-transparent">
                        <img
                          alt="bank"
                          src={globalImages.bank}
                          style={{
                            width: 30,
                            height: 30,
                            aspectRatio: "1",
                            objectFit: "cover",
                            position: "relative",
                            backgroundColor: "red",
                          }}
                        />
                      </span>
                    </div>
                    <div className="col-5 text-center">
                      <span className="font-weight-bold text-sm">
                        {bankData?.bank_account?.bank_name}
                      </span>{" "}
                      <br />
                      <span className="text-sm">
                        ************{bankData?.bank_account?.last4}
                      </span>{" "}
                    </div>
                    <div className="col-4 d-center">
                      <button
                        className="btn btn-default px-2 py-1"
                        onClick={() => deletePayoutAccount()}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {!bankData?.bank_account?.bank_name && (
                <div
                  className="card-body pt-3 d-center border-top cursor-pointer"
                  style={{
                    minHeight:
                      w9Pending || w9Rejected || w9Approved || selectedFile
                        ? "131px"
                        : "99px",
                  }}
                  onClick={() => createPayoutAccount()}
                >
                  <i className="fa fa-plus-circle mr-2 text-xl text-default"></i>
                  <div className="text-center">
                    <span className="text-underline" style={{ fontSize: 20 }}>
                      {bankData?.account_setup_status === 'restricted' ? 'Finish your account setup' : 'Link a new Account'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* withdraw amount */}
          <WithdrawAmount
            bank={bankData}
            user={currentUser}
            handleWithdraw={handleWithdraw}
            withdrawAmount={withdrawAmount}
            setWithdrawAmount={setWithdrawAmount}
          />
        </div>
      </div>
      {isLoading && <DotPulseBackdrop />}
    </div>
  );
}

export default AuthenticatedLayout(InitiateWithdraw);
